import React, { useEffect, useState } from "react";
import { Seo } from "@/components/Seo";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { VOLUNTEER } from "@/constants/testIds";
import { Clock, Plus, Flame, ArrowRight, Sprout } from "lucide-react";
import { toast } from "sonner";
import { maybeDonationNudge } from "@/lib/donate";
import { PendingFeedbackStrip } from "@/components/dashboard/PendingFeedbackStrip";
import { VolunteerHome } from "@/components/dashboard/VolunteerHome";
import { OrgHome } from "@/components/dashboard/OrgHome";
import { TierProgressRing } from "@/components/TierBadge";
import { TierNudge } from "@/components/dashboard/TierNudge";
import { Progress } from "@/components/ui/progress";
import { progressToNext } from "@/lib/badges";
import { CelebrationHost } from "@/components/CelebrationHost";
import { ReferralWelcomeBanner } from "@/components/ReferralWelcomeBanner";
import { AssessorRequestsCard } from "@/components/dashboard/AssessorRequestsCard";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({ total_hours: 0, logs_count: 0, orgs_count: 0 });
  const [hours, setHours] = useState([]);
  const [pending, setPending] = useState([]);
  const [myOpps, setMyOpps] = useState([]);
  const [apps, setApps] = useState([]);
  const [matched, setMatched] = useState([]);
  const [recs, setRecs] = useState({ items: [], reason: null });
  const [inspiration, setInspiration] = useState([]);
  const [verifyForm, setVerifyForm] = useState({ website: "", registration_number: "", note: "" });
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [alertRadius, setAlertRadius] = useState(String(user?.alert_radius_miles ?? 5));
  const [hosted, setHosted] = useState([]);
  const [hostPending, setHostPending] = useState([]);

  const isOrg = user?.user_type === "organisation";

  useEffect(() => {
    if (user?.alert_radius_miles != null) setAlertRadius(String(user.alert_radius_miles));
  }, [user?.alert_radius_miles]);

  useEffect(() => {
    if (!user) return;
    if (!isOrg) {
      api.myStats().then(setStats).catch(() => {});
      api.myHours().then(setHours).catch(() => {});
      api.myApplications().then(setApps).catch(() => {});
      api.matchedForMe().then(setMatched).catch(() => setMatched([]));
      api.recommendations({ limit: 5 }).then((r) => {
        setRecs(r);
        if (r?.reason === "profile_incomplete") {
          const params = { limit: 2 };
          if (user.lat != null && user.lng != null) {
            params.lat = user.lat; params.lng = user.lng;
          }
          api.opportunities(params).then((d) => setInspiration(Array.isArray(d) ? d.slice(0,2) : (d?.items || []).slice(0,2))).catch(() => setInspiration([]));
        }
      }).catch(() => setRecs({ items: [], reason: null }));
      api.myOpportunities().then(setHosted).catch(() => setHosted([]));
      api.pendingHours().then(setHostPending).catch(() => setHostPending([]));
    } else {
      api.pendingHours().then(setPending).catch(() => {});
      api.myOpportunities().then(setMyOpps).catch(() => {});
      api.orgApplications().then(setApps).catch(() => {});
    }
  }, [user, isOrg]);

  const approve = async (id) => {
    await api.approveHours(id);
    setPending((p) => p.map(x => x.id === id ? { ...x, status: "approved" } : x));
    toast.success("Hours approved");
    maybeDonationNudge("Thanks for confirming those hours! CivicHearth is free for organisations of every kind — a donation keeps it that way.", nav);
  };
  const reject = async (id) => {
    await api.rejectHours(id);
    setPending((p) => p.map(x => x.id === id ? { ...x, status: "rejected" } : x));
    toast("Hours rejected");
  };

  // Item 6 (2026-07): bulk approval — one call, optimistic state update.
  const bulkApprove = async (ids) => {
    try {
      const res = await api.bulkApproveHours(ids);
      setPending((p) => p.map(x => ids.includes(x.id) ? { ...x, status: "approved" } : x));
      toast.success(`${res.approved} log${res.approved === 1 ? "" : "s"} approved`);
      maybeDonationNudge("Thanks for confirming those hours! CivicHearth is free for organisations of every kind — a donation keeps it that way.", nav);
    } catch {
      toast.error("Could not approve logs");
    }
  };

  const applyToOpp = async (oppId) => {
    try {
      const res = await api.apply(oppId);
      toast.success(res.already_applied ? "You've already applied" : "Application sent");
    } catch {
      toast.error("Could not apply");
    }
  };

  const submitVerification = async () => {
    await api.requestVerification(verifyForm);
    setVerifyOpen(false);
    await refresh();
    toast.success("Verification request submitted");
  };

  const saveAlerts = async (val) => {
    setAlertRadius(val);
    await api.setAlertPrefs(Number(val));
    toast.success(Number(val) > 0 ? `Local alerts on — within ${val} miles` : "Local alerts turned off");
  };

  const withdrawApp = async (a) => {
    try {
      await api.withdraw(a.opportunity_id);
      setApps(list => list.filter(x => x.id !== a.id));
      toast("Application withdrawn");
    } catch {
      toast.error("Could not withdraw");
    }
  };

  const approveHost = async (id) => {
    await api.approveHours(id);
    setHostPending((p) => p.map(x => x.id === id ? { ...x, status: "approved" } : x));
    toast.success("Hours approved");
    maybeDonationNudge("Thanks for confirming those hours! A small donation keeps CivicHearth running for everyone.", nav);
  };
  const rejectHost = async (id) => {
    await api.rejectHours(id);
    setHostPending((p) => p.map(x => x.id === id ? { ...x, status: "rejected" } : x));
    toast("Hours rejected");
  };

  if (!user) return null;

  return (
    <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-6 py-10">
      <Seo title="Dashboard" />
      {!isOrg && <CelebrationHost />}
      <ReferralWelcomeBanner />
      {/* Item 5 (2026-07): volunteer gamification (streak/invite) lives in the
          NudgeStrip inside VolunteerHome — orgs never see it. Orgs keep the
          professional feedback prompt strip; volunteers get it as a chip. */}
      {isOrg && <PendingFeedbackStrip />}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Welcome back</div>
          <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">
            {user.name}
            {!isOrg && <TierProgressRing hours={stats.total_hours} />}
          </h1>
          <div className="text-neutral-600 mt-1 text-sm capitalize">{user.user_type} · {user.location || "Set your location"}</div>
          {/* Item 4: the old "Current tier" tile merged into the header —
              tier appears exactly once on the page. */}
          {!isOrg && (() => {
            const prog = progressToNext(stats.total_hours);
            return (
              <div data-testid="header-tier-progress" className="mt-3 max-w-xs">
                <Progress value={prog.pct} className="h-1.5" />
                <div className="text-xs text-neutral-500 mt-1.5" data-testid="header-tier-next">
                  {prog.next
                    ? <>{prog.next.threshold - stats.total_hours}h to <span className="font-medium text-forest">{prog.next.label}</span></>
                    : "Top tier reached — thank you!"}
                </div>
              </div>
            );
          })()}
        </div>
        <div className="flex gap-2">
          {isOrg ? (
            <>
              <Link to={`/o/${user.user_id}/impact`}><Button variant="outline" className="rounded-full" data-testid="nav-impact">Impact page</Button></Link>
              <Link to="/analytics"><Button variant="outline" className="rounded-full" data-testid="nav-analytics">Analytics</Button></Link>
              <Button data-testid={VOLUNTEER.newOppOpenBtn} onClick={() => nav("/post-opportunity")} className="rounded-full bg-forest hover:bg-forest-hover">
                <Plus className="w-4 h-4 mr-1.5" /> New opportunity
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => nav("/post-opportunity")} className="rounded-full" data-testid="host-event-btn">
                <Sprout className="w-4 h-4 mr-1.5" /> Host event
              </Button>
              <Button data-testid={VOLUNTEER.logHoursOpenBtn} onClick={() => nav("/log-hours")} className="rounded-full bg-forest hover:bg-forest-hover">
                <Clock className="w-4 h-4 mr-1.5" /> Log hours
              </Button>
            </>
          )}
        </div>
      </div>

      {!isOrg && <TierNudge stats={stats} matched={matched} recs={recs} />}

      {/* Item 4: the volunteer swipe CTA merged into "Find your next cause"
          (VolunteerHome). Orgs keep their candidate-swiping entry card. */}
      {isOrg && (
        <div
          data-testid="discover-entry-card"
          onClick={() => nav("/discover")}
          className="mt-8 relative overflow-hidden rounded-2xl bg-forest text-white p-6 sm:p-7 cursor-pointer group"
        >
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-terracotta/20 group-hover:scale-125 transition-transform duration-500" />
          <div className="relative flex items-center gap-5 flex-wrap">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center shrink-0">
              <Flame className="w-6 h-6 text-terracotta" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-display text-xl">Swipe through candidates</div>
              <div className="text-white/70 text-sm mt-0.5">Swipe through applicants & interested volunteers for a quick match.</div>
            </div>
            <Button className="rounded-full bg-terracotta hover:bg-terracotta-hover shrink-0" data-testid="discover-entry-btn">
              Start swiping <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      )}

      {!isOrg && (
        <VolunteerHome
          user={user} stats={stats} hours={hours} apps={apps} matched={matched}
          recs={recs} inspiration={inspiration} hosted={hosted} hostPending={hostPending}
          alertRadius={alertRadius} saveAlerts={saveAlerts}
          applyToOpp={applyToOpp} withdrawApp={withdrawApp}
          approveHost={approveHost} rejectHost={rejectHost}
        />
      )}

      {isOrg && <AssessorRequestsCard />}

      {isOrg && (
        <OrgHome
          user={user} myOpps={myOpps} apps={apps} pending={pending}
          approve={approve} reject={reject} bulkApprove={bulkApprove}
          verifyOpen={verifyOpen} setVerifyOpen={setVerifyOpen}
          verifyForm={verifyForm} setVerifyForm={setVerifyForm}
          submitVerification={submitVerification}
        />
      )}
    </div>
  );
}
